import type { ContactShareState, RelatedBulletin } from '../types';

export type DealStage =
  | 'chatting'
  | 'await_student_decision'
  | 'await_close'
  | 'ready_contacts'
  | 'contacts_pending'
  | 'contacts_shared'
  | 'await_review'
  | 'rejected'
  | 'none';

export function resolveDealStage(
  bulletin: RelatedBulletin | null | undefined,
  contactShare: ContactShareState | null | undefined,
): DealStage {
  if (!bulletin) return 'none';

  const status = (bulletin.status || '').toLowerCase();

  if (status === 'rejected') return 'rejected';

  if (contactShare?.shared) {
    if (contactShare.has_left_review) {
      return 'contacts_shared';
    }
    return 'await_review';
  }

  if (contactShare?.eligible) {
    if (contactShare.user_consented && !contactShare.other_consented) {
      return 'contacts_pending';
    }
    return 'ready_contacts';
  }

  if (status === 'accepted') {
    if (!bulletin.bulletin_closed) return 'await_close';
    return 'ready_contacts';
  }

  if (status === 'pending') return 'await_student_decision';

  return 'chatting';
}

export const DEAL_STEPS = [
  { key: 'chat', label: 'Переписка' },
  { key: 'decision', label: 'Согласование' },
  { key: 'close', label: 'Закрытие заявки' },
  { key: 'contacts', label: 'Контакты' },
] as const;

export function activeStepIndex(stage: DealStage): number {
  switch (stage) {
    case 'chatting':
      return 1;
    case 'await_student_decision':
      return 2;
    case 'await_close':
      return 3;
    case 'ready_contacts':
    case 'contacts_pending':
    case 'contacts_shared':
    case 'await_review':
      return 4;
    default:
      return 0;
  }
}

/** Contacts stage reached: mutual share done (review pending or already left). */
export function isContactsSharedDealStage(stage: DealStage): boolean {
  return stage === 'contacts_shared' || stage === 'await_review';
}

/**
 * Student should see "Оставить отзыв" after contacts are shared.
 * Prefer API `shared`; also trust deal stage (contacts_shared / await_review)
 * so the CTA cannot disappear when contact_share query is briefly stale.
 */
export function shouldShowReviewCta(
  role: 'student' | 'tutor' | string | null | undefined,
  contactShare: ContactShareState | null | undefined,
  stage: DealStage,
): boolean {
  if (role !== 'student') {
    return false;
  }
  if (contactShare?.shared) {
    return true;
  }
  return isContactsSharedDealStage(stage);
}

/** Button enabled only when student may still submit a review. */
export function canSubmitReview(
  contactShare: ContactShareState | null | undefined,
): boolean {
  if (!contactShare?.shared) {
    return false;
  }
  if (contactShare.has_left_review) {
    return false;
  }
  if (contactShare.can_leave_review === false) {
    return false;
  }
  if (contactShare.review_eligible_now === false) {
    return false;
  }
  return true;
}
